import { Helmet } from "react-helmet-async";

interface ServiceInfo {
  name: string;
  description: string;
  provider?: string;
  areaServed?: string;
  url?: string;
}

interface FAQItem {
  question: string;
  answer: string;
}

interface AIReadabilityBlockProps {
  serviceName: string;
  serviceDescription: string;
  keyPoints?: string[];
  faqs?: FAQItem[];
  serviceType?: "LegalService" | "ProfessionalService" | "Service";
  areaServed?: string;
  provider?: {
    name: string;
    telephone: string;
    address: {
      streetAddress: string;
      addressLocality: string;
      addressRegion: string;
      postalCode: string;
    };
  };
  className?: string;
}

/**
 * AIReadabilityBlock Component
 * 
 * Optimizes page content for AI/LLM search engines by providing:
 * 1. Structured JSON-LD data (Service + FAQPage schemas)
 * 2. Hidden semantic content block for AI crawlers
 * 3. Clean, extractable key facts
 * 
 * Based on best practices from:
 * - Schema.org structured data
 * - LLM-friendly content formatting
 * - Answer Engine Optimization (AEO)
 */
const AIReadabilityBlock = ({
  serviceName,
  serviceDescription,
  keyPoints = [],
  faqs = [],
  serviceType = "LegalService",
  areaServed = "Houston, Texas",
  provider = {
    name: "Daniel Albert Law Firm",
    telephone: "(832) 930-3059",
    address: {
      streetAddress: "5225 Katy Freeway, Suite 500",
      addressLocality: "Houston",
      addressRegion: "TX",
      postalCode: "77007"
    }
  },
  className = ""
}: AIReadabilityBlockProps) => {
  // Generate Service Schema
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": serviceType,
    "@id": `https://danielalbertlaw.com/practice-areas/${serviceName.toLowerCase().replace(/\s+/g, '-')}#service`,
    "name": serviceName,
    "description": serviceDescription,
    "provider": {
      "@type": "LegalService",
      "name": provider.name,
      "telephone": provider.telephone,
      "address": {
        "@type": "PostalAddress",
        ...provider.address,
        "addressCountry": "US"
      },
      "areaServed": {
        "@type": "City",
        "name": areaServed
      },
      "priceRange": "$$",
      "paymentAccepted": ["Cash", "Credit Card", "Check", "Zelle"],
      "availableLanguage": ["English", "Spanish", "Vietnamese"]
    },
    "areaServed": {
      "@type": "City",
      "name": areaServed
    },
    "hasOfferCatalog": keyPoints.length > 0 ? {
      "@type": "OfferCatalog",
      "name": `${serviceName} Services`,
      "itemListElement": keyPoints.map((point, index) => ({
        "@type": "Offer",
        "position": index + 1,
        "itemOffered": {
          "@type": "Service",
          "name": point
        }
      }))
    } : undefined
  };

  // Generate FAQ Schema if FAQs provided
  const faqSchema = faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `https://danielalbertlaw.com/practice-areas/${serviceName.toLowerCase().replace(/\s+/g, '-')}#faq`,
    "mainEntity": faqs.map((faq, index) => ({
      "@type": "Question",
      "@id": `https://danielalbertlaw.com/practice-areas/${serviceName.toLowerCase().replace(/\s+/g, '-')}#q-${index + 1}`,
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "@id": `https://danielalbertlaw.com/practice-areas/${serviceName.toLowerCase().replace(/\s+/g, '-')}#a-${index + 1}`,
        "text": faq.answer
      }
    }))
  } : null;

  return (
    <>
      {/* JSON-LD Structured Data for Search Engines */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
        {faqSchema && (
          <script type="application/ld+json">
            {JSON.stringify(faqSchema)}
          </script>
        )}
      </Helmet>

      {/* AI-Readable Content Block - Semantic HTML for LLM extraction */}
      <section 
        className={`py-10 bg-muted/20 ${className}`}
        aria-label={`${serviceName} Quick Facts`}
        data-ai-section="service-summary"
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Service Summary Header */}
            <header className="mb-6">
              <h2 className="text-2xl font-bold text-primary mb-2">
                {serviceName} - Key Information
              </h2>
              <p className="text-muted-foreground">{serviceDescription}</p>
            </header>

            {/* Key Points List - Optimized for AI extraction */}
            {keyPoints.length > 0 && (
              <div className="bg-background rounded-lg border border-border p-6 mb-6" data-ai-content="key-facts">
                <h3 className="font-semibold text-lg mb-4 text-foreground">
                  What You Need to Know
                </h3>
                <ul className="space-y-3" role="list">
                  {keyPoints.map((point, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span 
                        className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-primary text-primary-foreground text-sm font-medium flex-shrink-0"
                        aria-hidden="true"
                      >
                        {index + 1}
                      </span>
                      <span className="text-foreground">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* FAQ Section - Clear Q&A format for LLMs */}
            {faqs.length > 0 && (
              <div className="bg-background rounded-lg border border-border p-6" data-ai-content="faqs">
                <h3 className="font-semibold text-lg mb-4 text-foreground">
                  Frequently Asked Questions
                </h3>
                <dl className="space-y-4">
                  {faqs.map((faq, index) => (
                    <div key={index} className="border-b border-border pb-4 last:border-0 last:pb-0">
                      <dt className="font-medium text-foreground mb-2">
                        Q: {faq.question}
                      </dt>
                      <dd className="text-muted-foreground pl-4">
                        A: {faq.answer}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}

            {/* Contact Information - Always include for AI extraction */}
            <div className="mt-6 p-4 bg-primary/5 rounded-lg border border-primary/20" data-ai-content="contact">
              <p className="text-sm text-muted-foreground">
                <strong>Contact {provider.name}:</strong>{" "}
                <span itemProp="telephone">{provider.telephone}</span> |{" "}
                <span itemProp="address">{provider.address.streetAddress}, {provider.address.addressLocality}, {provider.address.addressRegion} {provider.address.postalCode}</span>
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                <strong>Languages:</strong> English, Spanish, Vietnamese |{" "}
                <strong>Service Area:</strong> {areaServed} and surrounding areas
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default AIReadabilityBlock;
